import { useEffect, useMemo, useState } from "react";
import { SkeletonCard } from "@web-ui/components/Skeleton";
import { PathnameProvider } from "@web-ui/adapters/navigation";
import { session, type Staff } from "@/api/admin";
import { AdminClient, BRANDS } from "@/api/client";
import { BrandMark } from "@/components/brand-mark";
import { SignIn } from "@/features/sign-in/sign-in";
import { applyBrandColors } from "@/lib/brand-style";
import { UploadButton } from "@/media/upload-button";
import { UploadProvider } from "@/media/upload-center";
import { rememberBrand, useRoute } from "@/lib/route";
import { BrandSwitcher } from "./brand-switcher";
import { ChangesPill } from "./changes-pill";
import { ConsoleFrame } from "./frame";
import { activeHref, consoleNavigation, showsLive } from "./navigation";
import { Screen, screenMeta } from "./screens";
import { StudioProvider, useStudio } from "./studio";

type SessionState = { kind: "loading" } | { kind: "signedOut" } | { kind: "signedIn"; staff: Staff };

/** Composition root: which brand, whose session, which screen. No feature state lives here. */
export function Console() {
  const route = useRoute();
  const clients = useMemo(() => new Map(BRANDS.map((b) => [b.id, new AdminClient(b)])), []);
  const client = clients.get(route.brand);
  const [sessions, setSessions] = useState<Record<string, SessionState>>({});
  const state = sessions[route.brand] ?? { kind: "loading" };
  const setState = (brand: string, s: SessionState) => setSessions((all) => ({ ...all, [brand]: s }));

  useEffect(() => { if (client) { applyBrandColors(client.brand.accent); rememberBrand(client.brand.id); document.title = `${client.brand.name} · Studio Console`; } }, [client]);

  useEffect(() => {
    if (!client || sessions[route.brand]) return;
    const brand = route.brand;
    session.current(client)
      .then((r) => setState(brand, { kind: "signedIn", staff: r.staff }))
      .catch(() => setState(brand, { kind: "signedOut" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, route.brand]);

  if (!client) return <main className="p-8"><p className="text-body-2">No brands are configured (deployments/).</p></main>;
  if (state.kind === "loading") return <main className="mx-auto flex min-h-svh max-w-xl flex-col justify-center p-4" aria-busy="true"><SkeletonCard label="Checking your session" /></main>;
  if (state.kind === "signedOut") return <SignIn key={client.brand.id} client={client} onSignedIn={(staff) => setState(client.brand.id, { kind: "signedIn", staff })} />;

  return (
    <StudioProvider key={client.brand.id} client={client} staff={state.staff}>
      <UploadProvider client={client}>
        <SignedIn onSignOut={() => void session.end(client).finally(() => setState(client.brand.id, { kind: "signedOut" }))} signedIn={(id) => (sessions[id]?.kind === "signedIn" ? true : undefined)} />
      </UploadProvider>
    </StudioProvider>
  );
}

function SignedIn({ onSignOut, signedIn }: { onSignOut: () => void; signedIn: (id: string) => boolean | undefined }) {
  const route = useRoute();
  const studio = useStudio();
  const { client, pack, staff, counts, brandView, isOwner } = studio;
  const navigation = consoleNavigation({ brand: client.brand.id, pack, counts, showLive: showsLive(brandView?.manifest.features, counts), isOwner, section: route.section });
  const meta = screenMeta(route, studio);
  const roleWord = staff.role === "owner" ? "Owner" : staff.role === "editor" ? "Editor" : "Viewer";

  return (
    <PathnameProvider pathname={activeHref(route)}>
      <ConsoleFrame
        title={meta.title}
        subtitle={meta.subtitle}
        mark={<><BrandMark brand={client.brand} current size="lg" /><span className="sr-only">{client.brand.name} home</span></>}
        top={<BrandSwitcher current={client.brand} signedIn={signedIn} />}
        navigation={navigation}
        actions={<>{studio.canEdit ? <UploadButton /> : null}<ChangesPill current={route.section === "review"} /></>}
        user={{ name: staff.email, detail: `${roleWord} · ${client.brand.name}`, items: [], logoutLabel: "Sign out", onLogout: onSignOut }}
      >
        <Screen route={route} />
      </ConsoleFrame>
    </PathnameProvider>
  );
}
